import React, { useState } from "react";
import { ArrowLeft, Code, Globe, Monitor, Smartphone } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import logo from "../logo.svg";

const WHATSAPP_NUMBER = "56928232048";
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const websites = [
  { name: "EunaStudio", short: "EU", logoBaseName: "eunastudio", url: "https://eunastudio.cynaxia.com" },
  { name: "Dosis Utiles", short: "DU", logoBaseName: "dosis-utiles", url: "https://dosis-utiles.web.app" },
  { name: "Cesmed + Salud", short: "CS", logoBaseName: "cesmed", url: "https://cesmedmasalud.cl" },
  { name: "Recargame", short: "RE", logoBaseName: "hangmed", url: "https://rechargetoome.web.app" },
  { name: "Operox", short: "RE", logoBaseName: "operox", url: "https://operox.cynaxia.com" },
  { name: "Cynaxia cloud", short: "RE", logoBaseName: "cloud", url: "https://cloud.cynaxia.com" }


];

const mobileApps = [
  {
    name: "EunaStudio",
    short: "EU",
    logoBaseName: "eunastudio",
    url: "https://play.google.com/store/apps/details?id=com.medstudio.eunastudio&pcampaignid=web_share"
  },
  {
    name: "Dosis Utiles",
    short: "DU",
    logoBaseName: "dosis-utiles",
    url: "https://play.google.com/store/apps/details?id=com.medstudio.dosisutiles&pcampaignid=web_share"
  },
  {
    name: "MedCross",
    short: "MC",
    logoBaseName: "medcross",
    url: "https://play.google.com/store/apps/details?id=com.cynaxia.medcross&pcampaignid=web_share"
  },
  {
    name: "Hangmed",
    short: "HM",
    logoBaseName: "hangmed",
    url: "https://play.google.com/store/apps/details?id=com.medstudio.ahorcado&pcampaignid=web_share"
  }
];

const serviceConfig = {
  "sitios-web": {
    title: "Sitios Web creados por Cynaxia Spa",
    icon: Globe,
    iconColor: "text-blue-400",
    whatsappLabel: "Sitios Web",
    type: "list",
    items: websites,
    itemLabel: "Abrir sitio web"
  },
  "apps-moviles": {
    title: "Apps móviles",
    icon: Smartphone,
    iconColor: "text-green-400",
    whatsappLabel: "Apps Móviles",
    type: "list",
    items: mobileApps,
    itemLabel: "Ver en Play Store"
  },
  "software-medida": {
    title: "Software a medida",
    icon: Code,
    iconColor: "text-red-400",
    whatsappLabel: "Software a Medida",
    type: "construction",
    description:
      "Estamos construyendo esta sección para mostrar casos y tecnologías recomendadas para desarrollo a medida."
  },
  "apps-escritorio": {
    title: "Apps de escritorio",
    icon: Monitor,
    iconColor: "text-yellow-400",
    whatsappLabel: "Apps de Escritorio",
    type: "construction",
    description:
      "Esta sección se encuentra en construcción. Muy pronto publicaremos las herramientas de escritorio disponibles."
  }
};

const createServiceWhatsAppUrl = (service) =>
  `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(`Hola Cynaxia, quiero solicitar el servicio de ${service}.`)}`;

const ProductLogo = ({ item }) => {
  const logoCandidates = item.logoBaseName
    ? [
        `/app-icons/${item.logoBaseName}.png`,
        `/app-icons/${item.logoBaseName}.webp`,
        `/app-icons/${item.logoBaseName}.jpg`,
        `/app-icons/${item.logoBaseName}.jpeg`,
        `/app-icons/${item.logoBaseName}.svg`
      ]
    : [];
  const [candidateIndex, setCandidateIndex] = useState(0);
  const currentLogo = logoCandidates[candidateIndex];

  if (!currentLogo) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 font-bold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
        {item.short}
      </div>
    );
  }

  return (
    <img
      src={currentLogo}
      alt={`Icono de ${item.name}`}
      onError={() => setCandidateIndex((prev) => prev + 1)}
      className="h-11 w-11 shrink-0 rounded-xl border border-slate-200 object-cover dark:border-slate-700"
      loading="lazy"
    />
  );
};

const ServicePage = () => {
  const { serviceId } = useParams();
  const service = serviceConfig[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-5">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Logo Cynaxia" className="h-9 w-9" />
              <span className="text-lg font-bold tracking-wide text-amber-500 dark:text-amber-400">Cynaxia</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:border-amber-500 hover:text-amber-700 dark:border-slate-700 dark:text-slate-100 dark:hover:border-amber-400 dark:hover:text-amber-300"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Link>
          </div>
        </header>
        <div className="px-6 py-20">
        <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-900">
          <h1 className="text-2xl font-bold">Servicio no encontrado</h1>
          <Link to="/" className="mt-4 inline-block text-amber-600 underline dark:text-amber-300">
            Volver al inicio
          </Link>
        </div>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-5">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Logo Cynaxia" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-wide text-amber-500 dark:text-amber-400">Cynaxia</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:border-amber-500 hover:text-amber-700 dark:border-slate-700 dark:text-slate-100 dark:hover:border-amber-400 dark:hover:text-amber-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-8 flex items-center gap-3">
            <Icon className={`h-6 w-6 ${service.iconColor}`} />
            <h1 className="text-2xl font-bold">{service.title}</h1>
          </div>

          {service.type === "list" ? (
            <div className="grid gap-4 md:grid-cols-2">
              {service.items.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 transition hover:border-amber-400 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:hover:border-amber-400/60"
                >
                  <ProductLogo item={item} />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100">{item.name}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-300">{service.itemLabel}</p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
              <p className="text-slate-700 dark:text-slate-200">{service.description}</p>
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-300">Estado: En construcción.</p>
            </div>
          )}

          <a
            href={createServiceWhatsAppUrl(service.whatsappLabel)}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500"
          >
            Solicitar servicio por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
